import _ from 'lodash';
import qs from 'qs';
import React from 'react';
import { useHistory } from 'react-router-dom';

import HideableSection from 'react-scale/corp/components/UI/HideableSection';
import { Button, TextInput, H2, Alert } from 'design-system';
import { HTTPError } from '@scale/frontend-lib/api-fetch';
import { trackError } from '@scale/frontend-lib/logging';
import { IUserJson } from '@scale/shared/types/user';
import useQueryParams from 'react-scale/corp/hooks/useQueryParams';

import { TaskerJourneyExplanation } from 'react-scale/corp/views/TaskerJourney/TaskerJourneyExplanation';

// TODO: more frontend-compatible interfaces for models
type AnyObject = Record<string, any>;

interface IQuery {
  project?: string;
  worker?: string;
}

interface IExplainPageProps {
  query: IQuery;
  error?: string | Error | HTTPError;
  user: IUserJson;
  history?: AnyObject;
}

interface IExplainPageState extends IQuery {}

class TaskerJourneyPage extends React.Component<IExplainPageProps, IExplainPageState> {
  state: IExplainPageState = {
    project: this.props.query.project || '',
    worker: this.props.query.worker || '',
  };

  renderError() {
    const { error } = this.props;

    if (!error) return null;

    if (typeof error === 'string') {
      return <Alert severity="error">Error: {error}</Alert>;
    }

    const { response } = error as HTTPError<{
      status: number;
      error: string;
    }>;

    if (!response) {
      return <p>{error.message}</p>;
    }

    return (
      <p>
        Error {response.status}: {response.error}
      </p>
    );
  }

  refreshQuery = () => {
    const { worker, project } = this.state;
    if (!project) {
      trackError('Need a project ID to use tasker journey');
      return;
    }
    const queryParams = _.pickBy({ worker, project });
    this.props.history?.push(`/tasker_journey?${qs.stringify(queryParams)}`);
    this.props.history?.go();
  };

  render() {
    const { error } = this.props;

    const validationError = getValidationError(this.state);

    return (
      <div>
        <HideableSection name="Overview">
          <div>
            <div className="overview">
              <TextInput
                fullWidth
                label="Worker"
                size="large"
                value={this.state.worker}
                onChange={e => this.setState({ worker: e.target.value })}
              />
              <TextInput
                fullWidth
                required
                label="Project"
                size="large"
                value={this.state.project}
                onChange={e => this.setState({ project: e.target.value })}
              />
              <Button
                type="success"
                onClick={() => this.refreshQuery()}
                disabled={!!validationError}
              >
                LET&#39;S GO
              </Button>
              <div className="overview-instructions">
                <H2>Tasker Journey</H2>
                <br />
                <b>Project: </b>
                Count of taskers that have passed each major phase of the project which can be
                filtered by date. Expanding results shows an abridged list of taskers that have
                completed that phase, which can then be used for individual explanation.
                <br />
                <br />
                <b>Project + Worker: </b>
                Shows the particular tasker's history on the project, including course completions,
                benchmarks, and task attempts.
              </div>
            </div>

            <style jsx>{`
              .overview {
                display: grid;
                grid-template-columns: 432px 432px;
                grid-template-rows: repeat(5, auto);
                gap: 16px;
                grid-column-gap: 32px;
              }
              .overview-instructions {
                grid-row: 1 / end;
                grid-column: 2;
              }
            `}</style>
          </div>
        </HideableSection>
        {error && <HideableSection name="Error">{this.renderError()}</HideableSection>}
        {!error && (
          <>
            {
              <TaskerJourneyExplanation
                workerId={this.state.worker}
                projectId={this.state.project}
              />
            }
          </>
        )}
      </div>
    );
  }
}

const getValidationError = ({ project }: IQuery) => {
  if (!project) return 'Enter project to get started.';
};

const TaskerJourneyPageWrapper: React.FC<{ user: IUserJson }> = ({ user }) => {
  const history = useHistory();

  const query: IQuery = useQueryParams();

  const validationError = getValidationError(query);

  if (validationError) {
    const props = {
      query,
      user,
      error: validationError,
    };

    return <TaskerJourneyPage history={history} {...props} />;
  }

  const props = {
    query,
    user,
  };
  return <TaskerJourneyPage history={history} {...props} />;
};

export default TaskerJourneyPageWrapper;
